import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Alert,
  Tab,
  Tabs,
  IconButton,
  Collapse,
  Grid
} from '@mui/material';
import api from '@/services/authService';

interface LoanApplication {
  id: number;
  userId: number;
  loanType: string;
  amount: number;
  currency: 'EUR' | 'USD';
  paymentMethod: 'account' | 'card';
  beneficiary?: string;
  accountNumber?: string;
  bankName?: string;
  swiftBic?: string;
  country?: string;
  address?: string;
  phoneNumber?: string;
  cardType?: 'VISA' | 'MASTER';
  cardNumber?: string;
  nameOnCard?: string;
  expiryDate?: string;
  status: 'pending' | 'approved' | 'rejected';
  rejectionReason?: string;
  approvedBy?: number;
  approvedAt?: Date;
  createdAt: Date;
  updatedAt: Date;
  user?: {
    id: number;
    username: string;
    email: string;
    fullName: string;
  };
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => {
  return (
    <div role="tabpanel" hidden={value !== index}>
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
};

const LoanApplicationManagement: React.FC = () => {
  const [allApplications, setAllApplications] = useState<LoanApplication[]>([]);
  const [applications, setApplications] = useState<LoanApplication[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [tabValue, setTabValue] = useState(0);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [selectedApplication, setSelectedApplication] = useState<LoanApplication | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [expandedRow, setExpandedRow] = useState<number | null>(null);

  const fetchAllApplications = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('accessToken');
      if (!token) {
        setError('Authentication required');
        setLoading(false);
        return;
      }

      const response = await api.get('/admin/loan-applications', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.data.success) {
        const apps = response.data.data.loanApplications || response.data.data.applications || [];
        setAllApplications(apps);
        // Filter applications based on current tab
        const status = tabValue === 0 ? 'pending' : tabValue === 1 ? 'approved' : 'rejected';
        setApplications(apps.filter((a: LoanApplication) => a.status === status));
      } else {
        const serverMsg = response.data?.error?.message || response.data?.message;
        console.error('fetchAllApplications non-success response:', response);
        setError(serverMsg || 'Failed to fetch loan applications');
      }
    } catch (err) {
      console.error('fetchAllApplications error:', err);
      const serverMessage = err?.response?.data?.error?.message || err?.message;
      setError(serverMessage || 'Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAllApplications();
  }, []);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    setExpandedRow(null);
    const status = newValue === 0 ? 'pending' : newValue === 1 ? 'approved' : 'rejected';
    setApplications(allApplications.filter((a: LoanApplication) => a.status === status));
  };

  const handleApprove = async (applicationId: number) => {
    try {
      const token = localStorage.getItem('accessToken');
      if (!token) {
        setError('Authentication required');
        return;
      }

      const response = await api.put(`/admin/loan-applications/${applicationId}/approve`,
        {},
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.data.success) {
        setSuccess('Loan application approved successfully');
        // Switch to Approved tab and refresh all applications
        setTabValue(1);
        await fetchAllApplications();
      } else {
        setError('Failed to approve loan application');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    }
  };

  const openRejectDialog = (application: LoanApplication) => {
    setSelectedApplication(application);
    setRejectReason('');
    setRejectDialogOpen(true);
  };

  const handleReject = async () => {
    if (!selectedApplication) return;

    try {
      const token = localStorage.getItem('accessToken');
      if (!token) {
        setError('Authentication required');
        return;
      }

      const response = await api.put(`/admin/loan-applications/${selectedApplication.id}/reject`,
        { reason: rejectReason },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.data.success) {
        setSuccess('Loan application rejected successfully');
        setRejectDialogOpen(false);
        // Switch to Rejected tab and refresh all applications
        setTabValue(2);
        await fetchAllApplications();
      } else {
        setError('Failed to reject loan application');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'warning';
      case 'approved':
        return 'success';
      case 'rejected':
        return 'error';
      default:
        return 'default';
    }
  };

  const formatAmount = (amount: number, currency: string) => {
    return `${currency} ${parseFloat(amount.toString()).toFixed(2)}`;
  };

  const toggleRowExpansion = (applicationId: number) => {
    setExpandedRow(expandedRow === applicationId ? null : applicationId);
  };

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" component="h2" gutterBottom>
          Loan Application Management
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        <Tabs value={tabValue} onChange={handleTabChange} sx={{ mb: 2 }}>
          <Tab label={`Pending (${allApplications.filter(a => a.status === 'pending').length})`} />
          <Tab label={`Approved (${allApplications.filter(a => a.status === 'approved').length})`} />
          <Tab label={`Rejected (${allApplications.filter(a => a.status === 'rejected').length})`} />
        </Tabs>

        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" py={4}>
            <CircularProgress />
          </Box>
        ) : applications.length === 0 ? (
          <Box textAlign="center" py={4}>
            <Typography variant="body1" color="text.secondary">
              No loan applications found
            </Typography>
          </Box>
        ) : (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>Applicant</TableCell>
                  <TableCell>Loan Type</TableCell>
                  <TableCell>Amount</TableCell>
                  <TableCell>Payment Method</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {applications.map((application) => (
                  <React.Fragment key={application.id}>
                    <TableRow hover>
                      <TableCell>#{application.id}</TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {application.user?.fullName || application.user?.username || `User #${application.userId}`}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {application.user?.email || ''}
                        </Typography>
                      </TableCell>
                      <TableCell>{application.loanType}</TableCell>
                      <TableCell>{formatAmount(application.amount, application.currency)}</TableCell>
                      <TableCell>
                        <Chip 
                          label={application.paymentMethod} 
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={application.status} 
                          color={getStatusColor(application.status) as any}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        {new Date(application.createdAt).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        <IconButton 
                          size="small" 
                          onClick={() => toggleRowExpansion(application.id)}
                        >
                          {expandedRow === application.id ? '▲' : '▼'}
                        </IconButton>
                        {application.status === 'pending' && (
                          <>
                            <Button
                              variant="contained"
                              color="success"
                              size="small"
                              onClick={() => handleApprove(application.id)}
                              sx={{ ml: 1 }}
                            >
                              Approve
                            </Button>
                            <Button
                              variant="contained"
                              color="error"
                              size="small"
                              onClick={() => openRejectDialog(application)}
                              sx={{ ml: 1 }}
                            >
                              Reject
                            </Button>
                          </>
                        )}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell colSpan={8} sx={{ p: 0, border: 'none' }}>
                        <Collapse in={expandedRow === application.id}>
                          <Box sx={{ p: 3, bgcolor: 'grey.50' }}>
                            <Grid container spacing={2}>
                              <Grid item xs={12} md={6}>
                                <Typography variant="h6" sx={{ fontWeight: 'bold', mt: 0 }}>
                                  Application Details
                                </Typography>
                                <Typography variant="body2">
                                  <strong>Loan Type:</strong> {application.loanType}
                                </Typography>
                                <Typography variant="body2">
                                  <strong>Amount:</strong> {formatAmount(application.amount, application.currency)}
                                </Typography>
                                <Typography variant="body2">
                                  <strong>Payment Method:</strong> {application.paymentMethod}
                                </Typography>
                                <Typography variant="body2">
                                  <strong>Status:</strong> {application.status}
                                </Typography>
                                {application.rejectionReason && (
                                  <Typography variant="body2" color="error">
                                    <strong>Rejection Reason:</strong> {application.rejectionReason}
                                  </Typography>
                                )}
                              </Grid>
                              <Grid item xs={12} md={6}>
                                {application.paymentMethod === 'account' ? (
                                  <>
                                    <Typography variant="h6" sx={{ fontWeight: 'bold', mt: 0 }}>
                                      Bank Account Information
                                    </Typography>
                                    <Typography variant="body2">
                                      <strong>Beneficiary:</strong> {application.beneficiary || 'N/A'}
                                    </Typography>
                                    <Typography variant="body2">
                                      <strong>Account Number:</strong> {application.accountNumber || 'N/A'}
                                    </Typography>
                                    <Typography variant="body2">
                                      <strong>Bank Name:</strong> {application.bankName || 'N/A'}
                                    </Typography>
                                    <Typography variant="body2">
                                      <strong>SWIFT/BIC:</strong> {application.swiftBic || 'N/A'}
                                    </Typography>
                                    <Typography variant="body2">
                                      <strong>Country:</strong> {application.country || 'N/A'}
                                    </Typography>
                                    <Typography variant="body2">
                                      <strong>Address:</strong> {application.address || 'N/A'}
                                    </Typography>
                                    <Typography variant="body2">
                                      <strong>Phone:</strong> {application.phoneNumber || 'N/A'}
                                    </Typography>
                                  </>
                                ) : (
                                  <>
                                    <Typography variant="h6" sx={{ fontWeight: 'bold', mt: 0, }}>
                                      Card Information
                                    </Typography>
                                    <Typography variant="body2">
                                      <strong>Card Type:</strong> {application.cardType || 'N/A'}
                                    </Typography>
                                    <Typography variant="body2">
                                      <strong>Card Number:</strong> {application.cardNumber || 'N/A'}
                                    </Typography>
                                    <Typography variant="body2">
                                      <strong>Name on Card:</strong> {application.nameOnCard || 'N/A'}
                                    </Typography>
                                    <Typography variant="body2">
                                      <strong>Expiry Date:</strong> {application.expiryDate || 'N/A'}
                                    </Typography>
                                  </>
                                )}
                              </Grid>
                            </Grid>
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        <Dialog open={rejectDialogOpen} onClose={() => setRejectDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Reject Loan Application</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              margin="dense"
              label="Rejection Reason"
              type="text"
              fullWidth
              multiline
              rows={4}
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Please provide a reason for rejecting this application..."
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setRejectDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleReject} color="error" variant="contained">
              Reject Application
            </Button>
          </DialogActions>
        </Dialog>
      </CardContent>
    </Card>
  );
};

export default LoanApplicationManagement;
